import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import ProjectDetailsHeader from './ProjectDetailsHeader';
import { PROJECT_STATUS } from '../../interfaces/project';
import { DSButton } from '../../components/Button/Button';

import {
  DataAttributes,
  DescriptionAndPreview,
  GeneralSettings,
  Platforms,
  PrivacyAndTerms,
  Rating,
} from './Sections';
import { changeAppStatus, updateApp } from '../../services/DevPortalService';
import { createTemplateApp, isAppReadyForReview, nullifyUrls } from '../../helpers/app';
import { IUser } from '../../interfaces/user';
import {
  completeFileUpload,
  fetchPermissionsForm,
  generateFilePresignedUrl,
  uploadFile,
  uploadPermissionsForm,
} from '../../services/FileUploadService';
import { CreateAppError } from '../../interfaces/quickApp';
import StatusBarRow from './StatusBarRow';
import { ApplicationInterface } from '../../interfaces/application';

type PropsType = {
  project: ApplicationInterface;
  user?: IUser;
  onUpdate: (app: ApplicationInterface, refresh: boolean) => void;
  onPermissionSuccess: (id: string) => void;
  onError: () => void;
};

const DEFAULT_FORM = {
  info: {
    country: '',
    platforms: [],
  },
  rating: {
    rating: 'TBD',
  },
};

const getButtonText = (project: ApplicationInterface): string => {
  if (project.setup.status === PROJECT_STATUS.REVIEW) return 'Review';
  if (isAppReadyForReview(project)) return 'Submit';

  return 'Save';
};

const ProjectDetailsForm: React.FC<PropsType> = ({
  project,
  user,
  onUpdate,
  onError,
  onPermissionSuccess,
}) => {
  const formHook = useForm<ApplicationInterface>({
    defaultValues: project || DEFAULT_FORM,
  });

  const [activeSections, setActiveSections] = useState<string[]>([]);

  const onFileDownload = async () => {
    if (!project.info?.applicationId || !project.info?.version) return;

    try {
      const blob = await fetchPermissionsForm(project.info.applicationId, project.info.version);
      if (!blob) return;

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'transaction_request_form.xlsx';
      link.click();
    } catch (e) {
      onError();
    }
  };

  const onFileSubmit = async (file: File) => {
    if (!project.info?.applicationId || !project.info?.version) return;

    try {
      // @ts-ignore
      formHook.clearErrors('xlsFile');

      const res = await uploadPermissionsForm(
        project.info?.applicationId,
        project.info?.version,
        file,
      );
      if (res) {
        onPermissionSuccess(project.info.applicationId as string);
        return true;
      }
    } catch (e) {
      onError();
    }
  };

  const doUpdate = async (data: ApplicationInterface) => {
    if (!user || project.setup.status === PROJECT_STATUS.REVIEW) return;

    const template = createTemplateApp(project, user);
    const payload: ApplicationInterface = {
      ...template,
      ...data,
      info: {
        ...template.info,
        ...data.info,
      },
      setup: {
        ...template.setup,
        ...data.setup,
      },
    };

    data.info.platforms.forEach((platform) => {
      if (payload.info?.appLinks)
        payload.info.appLinks[platform.toLocaleLowerCase()] = data.info.website;
    });

    try {
      let res;
      let refresh = false;

      if (isAppReadyForReview(project)) {
        res = await changeAppStatus(project.info.applicationId as string, PROJECT_STATUS.REVIEW);
        refresh = true;
      } else {
        res = await updateApp(nullifyUrls(payload), project.info.applicationId);
      }

      const toUpdate = res.parsedBody?.app as ApplicationInterface;
      if (payload.assets) toUpdate.assets = payload.assets;

      onUpdate(toUpdate, refresh);
    } catch (error) {
      if (error.validationErrors?.length > 0) {
        error.validationErrors.forEach((err: CreateAppError) => {
          formHook.setError(err.field, { type: 'manual', message: err.error });
        });
      }
      onError();
    }
  };

  const onSectionClick = (section: string) => {
    const index = activeSections.findIndex((s) => s === section);

    if (index > -1) setActiveSections(activeSections.filter((s) => s !== section));
    else setActiveSections([...activeSections, section]);
  };

  const uploadIcon = async (file: File) => {
    if (!project.info.applicationId) return;

    try {
      const res = await generateFilePresignedUrl({
        id: project.info.applicationId,
        kind: 'appLogo',
      });
      if (res.parsedBody) {
        await uploadFile(file, res.parsedBody.contentUrl);
        const completeUpload = await completeFileUpload(res.parsedBody.fileId);

        if (completeUpload.parsedBody) {
          const updatedApp: ApplicationInterface = {
            ...project,
            ...formHook.getValues(),
            assets: {
              appLogoUrl: {
                fileId: completeUpload.parsedBody.fileId,
                contentUrl: completeUpload.parsedBody.contentUrl,
              },
            },
          };
          await doUpdate(updatedApp);
        }
      }
    } catch (error) {
      onError();
    }
  };

  const actionButtonText = getButtonText(project);
  const buttonDisabled = actionButtonText === 'Review';

  return (
    <form onSubmit={formHook.handleSubmit(doUpdate)}>
      <ProjectDetailsHeader project={project} />

      <StatusBarRow
        project={project}
        buttonText={actionButtonText}
        buttonDisabled={buttonDisabled}
      />

      <GeneralSettings
        formHook={formHook}
        onClick={() => onSectionClick('Settings')}
        expand={activeSections.includes('Settings')}
        appID={project?.info.applicationId || ''}
        namespace={project.setup.namespace}
      />
      <Platforms
        formHook={formHook}
        expand={activeSections.includes('Platforms')}
        onClick={() => onSectionClick('Platforms')}
      />
      <PrivacyAndTerms
        formHook={formHook}
        expand={activeSections.includes('Privacy')}
        onClick={() => onSectionClick('Privacy')}
      />
      <Rating
        formHook={formHook}
        expand={activeSections.includes('Rating')}
        onClick={() => onSectionClick('Rating')}
        existingRatings={project?.rating?.rating}
      />
      <DescriptionAndPreview
        formHook={formHook}
        expand={activeSections.includes('Description')}
        onClick={() => onSectionClick('Description')}
        onUpload={uploadIcon}
        logoUrl={project.assets?.appLogoUrl?.contentUrl}
      />
      <DataAttributes
        expand={activeSections.includes('Data')}
        onClick={() => onSectionClick('Data')}
        onDownload={onFileDownload}
        onSubmit={onFileSubmit}
        hasPermissionsForm={project.info.hasPermissionsForm}
      />

      <div className="ds-project-details-buttons">
        <DSButton className={buttonDisabled ? 'disabled' : ''} type="submit">
          {actionButtonText}
        </DSButton>
      </div>
    </form>
  );
};

export default ProjectDetailsForm;
